import { useState, useEffect, useMemo } from 'react';
import { Home, MapPin, BedDouble, BadgePoundSterling, ExternalLink, AlertTriangle, ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";
const FEED_URL = "https://paisley.org.uk/api/properties/feed";
const HUB_URL = "https://paisley.org.uk/properties";

const PLACEHOLDER_IMG = "https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/9bhssqo2_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2005_04_33%20PM.png";

const formatPrice = (rent) => {
  if (rent == null || rent === '') return null;
  if (typeof rent === 'number') return `£${rent.toLocaleString()} pcm`;
  const s = String(rent).trim();
  return /pcm|pm|month/i.test(s) ? s : `${s} pcm`;
};

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(FEED_URL, { method: 'GET', cache: 'no-store' });
        if (!res.ok) throw new Error(`Feed responded ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setProperties(Array.isArray(data) ? data : []);
        setErrored(false);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q) ||
      (p.area || '').toLowerCase().includes(q),
    );
  }, [properties, search]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="properties-page">
      <SEOPage.Properties />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">
            <Home className="h-3 w-3 mr-1" /> Properties to Let
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Properties to Let in Paisley
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Live rentals mirrored from Paisley.org.uk — the local rentals hub for Paisley & Renfrewshire.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by title, address, or area…"
              className="pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="properties-search-input"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="animate-pulse overflow-hidden">
                  <div className="h-48 bg-slate-200" />
                  <CardContent className="p-5">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-slate-100 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : errored ? (
            <Card className="max-w-2xl mx-auto" data-testid="properties-fallback">
              <CardContent className="p-10 text-center">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Live listings temporarily unavailable
                </h3>
                <p className="text-slate-600 mb-6">
                  See all properties at{' '}
                  <a href={HUB_URL} className="text-sky-600 hover:text-sky-700 font-medium underline">
                    paisley.org.uk/properties
                  </a>.
                </p>
                <a href={HUB_URL}>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="properties-fallback-btn">
                    View properties on Paisley.org.uk <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Home className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {properties.length === 0 ? 'No properties listed right now' : 'No properties match your search'}
              </h3>
              <p className="text-slate-500">
                {properties.length === 0 ? 'Check back soon — listings update live.' : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => {
                const price = formatPrice(p.rent_pcm ?? p.price);
                const beds = p.bedrooms ?? p.beds;
                const img = p.image || p.image_url || PLACEHOLDER_IMG;
                return (
                  <a
                    key={`${p.url || p.title}-${i}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid={`property-card-${i}`}
                  >
                    <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
                      <div className="relative h-48 bg-slate-100 overflow-hidden">
                        <img
                          src={img}
                          alt={p.title || 'Property'}
                          loading="lazy"
                          onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {price && (
                          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-md flex items-center gap-1 text-sm font-semibold text-slate-900">
                            <BadgePoundSterling className="h-3.5 w-3.5 text-sky-500" />
                            {price}
                          </div>
                        )}
                        {p.property_type && (
                          <Badge className="absolute top-3 left-3 bg-slate-900/80 text-white border-0 capitalize">
                            {p.property_type}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                          {p.title}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
                          {beds != null && (
                            <span className="flex items-center gap-1">
                              <BedDouble className="h-4 w-4 text-slate-400" />
                              {beds} bed{beds === 1 ? '' : 's'}
                            </span>
                          )}
                          {p.furnished && (
                            <span className="capitalize">· {p.furnished}</span>
                          )}
                        </div>
                        {(p.address || p.area) && (
                          <p className="text-sm text-slate-600 flex items-start gap-1">
                            <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{p.address || p.area}</span>
                          </p>
                        )}
                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                          <span>View on Paisley.org.uk</span>
                          <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>
          )}

          {/* Attribution */}
          {!loading && !errored && (
            <p className="text-center text-sm text-slate-500 mt-10" data-testid="properties-attribution">
              Listings provided by{' '}
              <a
                href="https://paisley.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:text-sky-700 underline font-medium"
              >
                Paisley.org.uk
              </a>{' '}
              — the local rentals hub for Paisley & Renfrewshire.
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-sky-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Got a property to let?
          </h2>
          <p className="text-sky-100 mb-6">
            List it on Paisley.org.uk and it appears here automatically.
          </p>
          <a href={HUB_URL} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 px-8">
              List on Paisley.org.uk <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
